// Libraries
import Moment from 'moment';

// ClientRoutes
import { ClientRoutes } from '../Routes';

/* eslint-disable no-undef */

/**********************************************************
 *  Variables
 **********************************************************/

var defaultParameters = {
    'currency' : 'BTC',
    'exchange' : 'Kraken',
    'quoteCurrency' : 'USD',
    'interval' : 'hour'
};

var defaultChartProperties = {
    // top level
    height : 500,
    width : 600,
    margin : {left: 10, right: 75, top:10, bottom: 20},
    yGrid : { 
        innerTickSize: -1 * (600-30-30),
        tickStrokeDasharray: 'Solid',
        tickStrokeOpacity: 0.2,
        tickStrokeWidth: 1,
        tickStroke: '#8798b2', // '#95a4bc'
        stroke: '#8798b2',
        fill: '#8798b2',
        fontSize: 14,
        fontFamily: 'Nunito, Helvetica Neue, Helvetica, Arial, sans-serif'
    },
    ticks : { x: 600/60, y: 400/60},
    xGrid :{ 
            innerTickSize: -1 * (400-10-20),
            tickStrokeDasharray: 'Solid',
            tickStrokeOpacity: 0.2,
            tickStrokeWidth: 1,
            tickStroke: '#8798b2',
            stroke: '#8798b2',
            fill: '#8798b2',
            fontSize: 14,
            fontFamily: 'Nunito, Helvetica Neue, Helvetica, Arial, sans-serif'
    },
    ohlcSettings : {
        fontSize: 13,
        textFill: '#BFCDE3',
        labelFill: '#8798b2',
        fontFamily: 'Nunito, Helvetica Neue, Helvetica, Arial, sans-serif',
        left: 6,
        top: 20
    },
    hoverSettings : {
        bgOpacity: 0.2,
        bgFill: "#8bcfef",
        fill: "rgba(0,0,0,0)",
        stroke: "rgba(0,0,0,0)",
        opacity: 0
    },
    mouseCoordinate : {
        fill: 'rgba(0,0,0,0)',
        arrowWidth: 0,
        fontSize: 14,
        rectWidth: 50,
        fontFamily: 'Nunito, Helvetica Neue, Helvetica, Arial, sans-serif',
        dx: 12
    },
    crossHairSettings : {
        stroke: '#8798b2',
        opacity: 0.3
    },
    candlesAppearance : {
        fill: function fill(d) {
          return d.close > d.open ? "rgba(196, 205, 211, 0)" : "rgba(239, 10, 82, 1.0)";
        },
        stroke: function stroke(d) {
            return d.close > d.open ? "#0aefaa" : "#ef0a52";
        },
        wickStroke: function wickStroke(d) {
            return d.close > d.open ? "#0aefaa" : "#ef0a52";
        },
        candleStrokeWidth: 1,
        widthRatio: 0.80,
        opacity: 1,
        number: 45
    },
    volumeHeight: 75,
    mouseMoveEvent: true,
    panEvent: true,
    zoomEvent: true,
    clamp: false,
    zoomAnchor: true
    
};

/**********************************************************
 *  Chart Parameters Functions
 **********************************************************/

// Set the given exchange, and fetch updated chart after
function setExchange(exchange) {

    //console.log('ChartStore.setExchange()');

    localStorage.setItem('settings_chart_exchange', exchange);

}

// Set the given currency, and fetch updated chart after
function setCurrency(currency) {
    
    //console.log('ChartStore.setCurrency()');

    localStorage.setItem('settings_chart_currency', currency);

}

    
// Set the given quote currency, and fetch updated chart after
function setQuoteCurrency(quoteCurrency) {
    
    //console.log('ChartStore.setQuoteCurrency()');

    localStorage.setItem('settings_chart_quoteCurrency', quoteCurrency);

}

// Set the given interval, and fetch updated chart after
function setInterval(interval) {
    
    //console.log('ChartStore.setCurrency()');

    localStorage.setItem('settings_chart_interval', interval);

}


// Sets all chart parameters
function setAllParameters(currency, exchange, quoteCurrency, interval) {

    //console.log('ChartStore.setAllParameters()');

    localStorage.clear();

    localStorage.setItem('settings_chart_currency', currency);
    localStorage.setItem('settings_chart_exchange', exchange);
    localStorage.setItem('settings_chart_quoteCurrency', quoteCurrency);
    localStorage.setItem('settings_chart_interval', interval);

}


// Returns all existing parameters.  If any null, return default value
function getAllParameters() {

    var parameters = {
        'currency' : localStorage.getItem('settings_chart_currency'),
        'exchange' : localStorage.getItem('settings_chart_exchange'),
        'quoteCurrency' : localStorage.getItem('settings_chart_quoteCurrency'),
        'interval' : localStorage.getItem('settings_chart_interval')
    };

    // ensure none of the parameters are null when retrieving chart
    // if so, insert default parameter
    for(let[key, value] of Object.entries(parameters)) {

        if(value === null)
            parameters[key] = defaultParameters[key]
    }

    return parameters;
}

function getChartProperties() {

    return defaultChartProperties;
}

// Returns the time that the chart was last updated
function isChartExpired() {

    const lastUpdated = JSON.parse(localStorage.getItem('chart_last_updated'));

    var response = false;

    if(lastUpdated === null)
        return true;
    else {
        response = Moment().diff(Moment(lastUpdated), 'minutes') > 5
        return response;
    }

}
    

// Clear all parameters from local storage
function clearChartData() {

    localStorage.clear();

}


/**********************************************************
 *  Fetch Chart
 **********************************************************/

// Retrieves the chart for the given parameters and stores in local storage as a strings
function fetchChart(numberCandles) {

    var parameters = {
        'currency' : localStorage.getItem('settings_chart_currency'),
        'exchange' : localStorage.getItem('settings_chart_exchange'),
        'quoteCurrency' : localStorage.getItem('settings_chart_quoteCurrency'),
        'interval' : localStorage.getItem('settings_chart_interval')
    };

    // ensure none of the parameters are null when retrieving chart
    // if so, insert default parameter and save to localStorage
    for(let[key, value] of Object.entries(parameters)) {

        if(value === null) {
            parameters[key] = defaultParameters[key];
            localStorage.setItem('settings_chart_' + key, defaultParameters[key]);
        }

    }
    
    return new Promise( (resolve, reject) => {

        ClientRoutes.getChart(parameters.exchange, parameters.currency, parameters.quoteCurrency, parameters.interval)
            .then( (newChart) => {

                localStorage.setItem('chart', JSON.stringify(newChart));
                localStorage.setItem('chart_last_updated', JSON.stringify(Moment().valueOf()));

                var chart = unpackChart( newChart, numberCandles );
                
                resolve(chart);

            })
            .catch( (error) => {
                localStorage.setItem('chart_last_updated', JSON.stringify(Moment().valueOf()));
                reject(error);
            });

    });
    
}


// Retrieves chart from local storage, unpacks/formats it and returns
function getChart(numberCandles) {

    var rawChart = JSON.parse(localStorage.getItem('chart'));

    // No existing chart
    if(rawChart === null) {
        return null;
    }
    else {
        var chart = unpackChart( rawChart, numberCandles );
        
        return chart;
    }

}


// Given a chart as an array of candles, set their datetime and determine
// start and end values
function unpackChart(rawChart, numberCandles) {

    if(rawChart === []) {
        fetchChart();
        return null;
    }

    var start = numberCandles < rawChart.length ? Moment.unix(rawChart[rawChart.length-numberCandles].date).toDate() : Moment.unix(rawChart[0].date).toDate();
    var end = padChart(Moment.unix(rawChart[rawChart.length-1].date), getAllParameters().interval); //Moment.unix(rawChart[rawChart.length-1].date).toDate();

    var updatedTimeCandles = rawChart.map(candle => {
        candle.date = Moment.unix(candle.date).toDate()
        return candle;
    });

    var chartObj = {
        chart : updatedTimeCandles,
        start : start,
        end : end
    };

    return chartObj;

}

// Given the last candlestick and interval,
// add a few extra candle spots to pad chart
function padChart(endDate, interval) {

    if(interval === 'minute')
        return endDate.add('m', 3).toDate();
    else if(interval === 'hour')
        return endDate.add('h', 3).toDate();
    else 
        return endDate.add('d', 3).toDate();

}


const ChartStore = { setExchange, setCurrency, setQuoteCurrency, setInterval, clearChartData, 
    setAllParameters, getAllParameters, getChart, getChartProperties, isChartExpired, fetchChart, padChart };
export default ChartStore;






// WEBPACK FOOTER //
// ./src/Stores/ChartStore.js